// Windows Event Log parser
//
// The service-manager agent's eventlog pump (pkg/servicemanager/agent/eventlog.go)
// publishes one JSON record per Windows Event Log entry into a NATS
// stream channel named "eventlog". Each Buffer's data is a complete
// EventLogRecord JSON document — no embedded newlines, no continuation
// semantics. The shape is:
//
//   {
//     "provider":    "A8SampleCounter",
//     "eventId":     1,
//     "level":       4,                 // 1=Critical 2=Error 3=Warning 4=Info 5=Verbose
//     "channel":     "Application",
//     "recordId":    15295,
//     "timeCreated": "2026-05-28T18:41:10.0357414Z",
//     "computer":    "win-staging-01",
//     "rawXml":      "<Event xmlns=...>...</Event>"
//   }
//
// The structured (accur8) parser treats anything without a leading
// HH:MM:SS timestamp as a continuation of the previous line, so it
// silently concatenates every JSON record into one giant LogEntry —
// the symptom is "I see one log message in the viewer despite many
// records on the wire." This parser sidesteps that path entirely:
// each line is one record, period.
//
// The rendered message extracts the human-readable bits from rawXml's
// <EventData><Data>...</Data></EventData> blocks when present (that's
// where most providers stash the actual message text). When EventData
// has no useful content we fall back to a `provider/eventId` summary.

import { LogEntry, LogLevel } from './types';

let logIdCounter = 0;

interface EventLogRecord {
  provider?: string;
  eventId?: number;
  level?: number;
  channel?: string;
  recordId?: number;
  timeCreated?: string;
  computer?: string;
  rawXml?: string;
}

// Windows Event Log levels → our internal log levels. Critical+Error
// both surface as ERROR since the viewer's filter chip only has one
// "error" bucket; Verbose maps to TRACE.
function mapLevel(eventLogLevel: number | undefined): LogLevel | undefined {
  switch (eventLogLevel) {
    case 1: return 'ERROR'; // Critical
    case 2: return 'ERROR';
    case 3: return 'WARN';
    case 4: return 'INFO';
    case 5: return 'TRACE'; // Verbose
    default: return undefined;
  }
}

// Pull the message text out of the rawXml. Most providers stuff their
// actual message into one or more <EventData><Data>...</Data></EventData>
// blocks. Concatenate them with spaces — simple and matches what
// Event Viewer's "Friendly View" shows in the General tab. Returns ""
// if there's no usable EventData.
function extractEventData(rawXml: string | undefined): string {
  if (!rawXml) return '';
  const out: string[] = [];
  // Match either <Data>text</Data> or <Data Name="X">text</Data>.
  const re = /<Data(?:\s+[^>]*)?>([^<]*)<\/Data>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rawXml)) !== null) {
    const value = m[1].trim();
    if (value) out.push(value);
  }
  return out.join(' ');
}

/**
 * Create a Windows Event Log parser. One Buffer = one JSON record =
 * one LogEntry. No continuation handling; rawLine is parsed in
 * isolation.
 */
export function createWindowsEventLogParser(sourceId?: string) {
  return {
    parseLine(rawLine: string, blockTimestamp?: Date): LogEntry[] {
      const line = rawLine.trim();
      if (!line) return [];

      let rec: EventLogRecord;
      try {
        rec = JSON.parse(line);
      } catch {
        // Malformed JSON — surface the raw text so the operator can
        // still see something went wrong upstream.
        return [{
          id: `${sourceId || 'eventlog'}-${++logIdCounter}`,
          timestamp: blockTimestamp || new Date(),
          originalLine: rawLine,
          message: rawLine,
          level: 'ERROR',
          source: sourceId,
          isMultiLine: false,
        }];
      }

      const messageText = extractEventData(rec.rawXml);
      const summary = messageText
        ? `${rec.provider ?? 'unknown'} (eventId=${rec.eventId ?? '?'}): ${messageText}`
        : `${rec.provider ?? 'unknown'} (eventId=${rec.eventId ?? '?'})`;

      let timestamp: Date | undefined;
      if (rec.timeCreated) {
        const parsed = new Date(rec.timeCreated);
        if (!isNaN(parsed.getTime())) {
          timestamp = parsed;
        }
      }
      if (!timestamp) timestamp = blockTimestamp || new Date();

      return [{
        id: `${sourceId || 'eventlog'}-${++logIdCounter}`,
        timestamp,
        originalLine: rawLine,
        message: summary,
        category: rec.channel,
        thread: rec.provider,
        level: mapLevel(rec.level),
        source: sourceId,
        isMultiLine: false,
        metadata: rec,
      }];
    },

    flush(): LogEntry[] {
      return [];
    },

    reset() {
      // no-op — every line is self-contained
    },
  };
}
